'use client';

import { Notification, SkeletonBar } from '@petsmart-ui/sparky';
import { usePathname } from 'next/navigation';
import type { MouseEvent } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useCookie } from 'react-use';

import { dataLayerPush, getBaseUrlJoined, layoutEvents } from '@libs/analytics';
import { useCart, useIsMounted } from '@libs/common-next';
import { NAVIGATION_URLS } from '@libs/core';

import './LegacyMinicart.scss';

export const trackingData = (data: object) => Object.assign(data, { ...layoutEvents.headerClick });

export default function LegacyMinicart() {
    const { cart, isLoading: isCartLoading } = useCart();
    const pathname = usePathname();
    const COOKIE_NAME = 'cart_quantity';
    const [cachedQty, updateCachedQty] = useCookie(COOKIE_NAME);
    const { isMounted } = useIsMounted();

    const [qty, setQty] = useState(cachedQty ? Number(cachedQty) : 0);

    const isOrderConfirmationPage = pathname === NAVIGATION_URLS.velocity.orderConfirmation;
    const isLoading = isCartLoading;

    useEffect(() => {
        if (isLoading) return;

        const currentQty = cart?.summary?.itemCount ?? 0;

        setQty(currentQty);
        updateCachedQty(String(currentQty));
    }, [cart?.summary?.itemCount, isLoading, updateCachedQty]);

    const handleClick = useCallback((e: MouseEvent<HTMLAnchorElement>) => {
        // Prevent default action of the link before data pushed into dataLayer
        e.preventDefault();

        dataLayerPush(
            trackingData({
                selection: 'cart',
                link_url: getBaseUrlJoined(NAVIGATION_URLS.legacy.cart),
            })
        );

        // Go to the link after data is pushed into dataLayer
        window.location.assign(NAVIGATION_URLS.legacy.cart);
    }, []);

    return (
        <div id="mini-cart" className="legacy-mini-cart" data-testid="legacy-cart-icon">
            <a
                title="View Cart"
                onClick={handleClick}
                aria-label="Shopping Cart"
                className="legacy-mini-cart__link"
                href={NAVIGATION_URLS.legacy.cart}
            >
                <span className="legacy-mini-cart__icon">
                    <svg id="a" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                        <path d="m15.19,10.83c.33,0,.63-.18.79-.47l3.91-7.01c.15-.28.15-.62-.02-.9-.16-.28-.46-.45-.78-.45H3.92l-.74-1.57c-.07-.13-.16-.24-.28-.32-.12-.07-.26-.11-.41-.11H.76c-.22,0-.4.07-.54.22-.15.14-.22.31-.22.52,0,.22.07.4.22.55.14.16.3101.23.52.23h1.24l3.87,8.17-1.72,3.19c-.52.96.17,2.11,1.26,2.11h11.86c.41,0,.75-.33.75-.75,0-.41-.34-.75-.75-.75H5.5l1.44-2.66h8.25ZM4.58,3.5h13.51l-3.25,5.83h-7.5l-2.76-5.83Zm3.2,14.75c0,.97-.79,1.75-1.75,1.75-.97,0-1.75-.78-1.75-1.75s.78-1.75,1.75-1.75c.96,0,1.75.78,1.75,1.75Zm10.17,0c0,.97-.79,1.75-1.75,1.75-.97,0-1.75-.78-1.75-1.75s.78-1.75,1.75-1.75c.96,0,1.75.78,1.75,1.75Z" />
                    </svg>
                </span>

                {/* Loader */}
                {!isOrderConfirmationPage && isLoading && <SkeletonBar className="legacy-mini-cart__loader" />}

                {/* Quantity */}
                {!isOrderConfirmationPage && isMounted && Boolean(qty) && (
                    <Notification
                        text={qty}
                        data-testid="notification"
                        className="legacy-mini-cart__notification"
                        screenReaderText={`${qty} items in your cart`}
                    />
                )}
            </a>
        </div>
    );
}
